import React, { useMemo, useState } from "react";
import {
  Chart,
  Title,
  Series,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "@highcharts/react";
import "../../css/calculators/MortgageBorrow.css";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";
import SecondaryButton from "../components/buttons/SecondaryButton";
import PrimaryButton from "../components/buttons/PrimaryButton";
import CustomSelect from "../components/CustomSelect";

const initialState = {
  investmentGoal: "",
  amountAlreadyInvested: "",
  singleAdditionalInvestment: "",
  monthlyInvestment: "",
  estimatedGrowth: "",
};

const ANNUAL_CHARGE = 0.75;
const MAX_MONTHS = 100 * 12;

const growthOptions = [
  { value: "", label: "Please select" },
  { value: "1.5", label: "Low 1.5%" },
  { value: "4.5", label: "Medium 4.5%" },
  { value: "7.5", label: "High 7.5%" },
];

const cleanNumericInput = (value) => value.replace(/[^\d.]/g, "");

const parseNumericValue = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  return Number(value) || 0;
};

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatYearsAndMonths = (months) => {
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years <= 0) {
    return `${remainingMonths} month${remainingMonths === 1 ? "" : "s"}`;
  }

  if (remainingMonths === 0) {
    return `${years} year${years === 1 ? "" : "s"}`;
  }

  return `${years} year${years === 1 ? "" : "s"} and ${remainingMonths} month${remainingMonths === 1 ? "" : "s"}`;
};

const buildGoalTimeProjection = ({
  targetAmount,
  existingInvestment,
  lumpSum,
  monthlyContribution,
  annualGrowthRate,
}) => {
  const goal = Number(targetAmount || 0);
  const startingValue = Number(existingInvestment || 0) + Number(lumpSum || 0);
  const monthlyInvestment = Number(monthlyContribution || 0);

  if (!goal || !annualGrowthRate) return null;

  const monthlyGrowthRate = annualGrowthRate / 100 / 12;
  const monthlyChargeRate = ANNUAL_CHARGE / 100 / 12;

  let fundValue = startingValue;
  let reachedMonth = fundValue >= goal ? 0 : null;

  const monthlyData = [
    {
      month: 0,
      value: Number(fundValue.toFixed(2)),
    },
  ];

  for (let monthIndex = 1; monthIndex <= MAX_MONTHS; monthIndex += 1) {
    fundValue += monthlyInvestment;
    fundValue *= 1 + monthlyGrowthRate;
    fundValue *= 1 - monthlyChargeRate;

    monthlyData.push({
      month: monthIndex,
      value: Number(fundValue.toFixed(2)),
    });

    if (fundValue >= goal) {
      reachedMonth = monthIndex;
      break;
    }
  }

  if (reachedMonth === null) {
    return null;
  }

  const finalValue = Number(monthlyData[reachedMonth].value.toFixed(2));
  const totalExisting = Number(existingInvestment || 0);
  const totalNewInvestments = Number(
    (Number(lumpSum || 0) + monthlyInvestment * reachedMonth).toFixed(2),
  );
  const investmentGrowth = Number(
    (finalValue - totalExisting - totalNewInvestments).toFixed(2),
  );

  const chartIndexes = Array.from(
    new Set([
      0,
      Math.round(reachedMonth / 3),
      Math.round((reachedMonth * 2) / 3),
      reachedMonth,
    ]),
  );

  const chartPoints = chartIndexes.map((index) => {
    const point = monthlyData[index];
    const pointYears = Math.floor(point.month / 12);
    const pointMonths = point.month % 12;

    return {
      label: `${pointYears} year${pointYears !== 1 ? "s" : ""}\n${pointMonths} month${pointMonths !== 1 ? "s" : ""}`,
      value: point.value,
    };
  });

  return {
    monthsToGoal: reachedMonth,
    yearsToGoal: Math.floor(reachedMonth / 12),
    remainingMonthsToGoal: reachedMonth % 12,
    durationLabel: formatYearsAndMonths(reachedMonth),
    targetAmount: goal,
    finalValue,
    totalExisting,
    totalNewInvestments,
    investmentGrowth,
    chartPoints,
  };
};

export default function InvestmentGoalTimeCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleCalculate = () => {
    setHasCalculated(true);
  };

  const handleClear = () => {
    setFormData(initialState);
    setHasCalculated(false);
  };

  const results = useMemo(() => {
    if (!hasCalculated) return null;

    return buildGoalTimeProjection({
      targetAmount: parseNumericValue(formData.investmentGoal),
      existingInvestment: parseNumericValue(formData.amountAlreadyInvested),
      lumpSum: parseNumericValue(formData.singleAdditionalInvestment),
      monthlyContribution: parseNumericValue(formData.monthlyInvestment),
      annualGrowthRate: parseNumericValue(formData.estimatedGrowth),
    });
  }, [formData, hasCalculated]);

  const canCalculate =
    formData.investmentGoal &&
    formData.estimatedGrowth &&
    (parseNumericValue(formData.amountAlreadyInvested) > 0 ||
      parseNumericValue(formData.singleAdditionalInvestment) > 0 ||
      parseNumericValue(formData.monthlyInvestment) > 0);

  const chartCategories = results?.chartPoints?.map((item) => item.label) || [];
  const chartData = results?.chartPoints?.map((item) => item.value) || [];

  return (
    <>
      <div className="borrow-form-wrap">
        <div className="borrow-field-group">
          <label className="borrow-label">Investment goal (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="investmentGoal"
              value={formData.investmentGoal}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  investmentGoal: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Amount invested already (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="amountAlreadyInvested"
              value={formData.amountAlreadyInvested}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  amountAlreadyInvested: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">
            Single additional investment (£)
          </label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="singleAdditionalInvestment"
              value={formData.singleAdditionalInvestment}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  singleAdditionalInvestment: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Amount invested per month (£)</label>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="monthlyInvestment"
              value={formData.monthlyInvestment}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  monthlyInvestment: raw,
                }))
              }
              className="borrow-input "
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <label className="borrow-label">Estimated growth (%)</label>
          <div className="select-wrapper">
            {/* <select
              name="estimatedGrowth"
              value={formData.estimatedGrowth}
              onChange={handleChange}
              className="borrow-input"
            >
              {growthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            <CustomSelect
              name="estimatedGrowth"
              options={growthOptions}
              value={formData.estimatedGrowth}
              onChange={handleChange}
              placeholder="Please select an estimated growth"
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            justifyContent: "flex-end",
          }}
        >
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>

        <div className="result-divider investment-form-divider" />
      </div>

      {hasCalculated && results && (
        <div className="borrow-result-wrap">
          <div className="result-container">
            <div className="title">Result</div>

            <div className="result-section">
              <p className="result-copy investment-top-copy">
                Charges assumed in the calculator are {ANNUAL_CHARGE}% per
                annum.
              </p>

              <p className="result-copy investment-top-copy">
                Charges are typically between 0.5% and 1.85% of the fund value
                per year depending on investments chosen and the amount
                invested.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">It could take you around</div>
              <div className="result-value">{results.durationLabel}</div>
            </div>

            <div className="result-section">
              <div className="result-heading">To reach your goal of</div>
              <div className="result-value">
                {formatCurrency(results.targetAmount, 0)}
              </div>

              <div className="investment-chart-card">
                <Chart
                  containerProps={{
                    style: { width: "100%", height: "360px" },
                  }}
                >
                  <Title>Investment value over time</Title>

                  <XAxis categories={chartCategories} />
                  <YAxis />

                  <Series
                    type="area"
                    name="Your investments"
                    data={chartData}
                  />

                  <Tooltip />
                  <Legend />
                </Chart>
              </div>
            </div>

            <h2 className="result-title investment-based-on-title">
              The results are based on:
            </h2>

            <div className="result-section">
              <div className="result-heading">Existing investments</div>
              <div className="result-value">
                {formatCurrency(results.totalExisting, 0)}
              </div>
            </div>

            <div className="result-section">
              <div className="result-heading">New investments</div>
              <div className="result-value">
                {formatCurrency(results.totalNewInvestments, 0)}
              </div>
              <p className="result-copy">
                Based on any single and/or regular payments you’ve selected.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">Investment growth</div>
              <div className="result-value">
                {formatCurrency(results.investmentGrowth, 0)}
              </div>
            </div>

            <div className="result-divider" />

            <div className="title result-note-title">PLEASE NOTE:</div>
            <p className="result-note-copy">
              Nothing on this site is personalised advice or a recommendation.
              If you need a personalised recommendation based on your personal
              circumstances you should seek financial advice.
            </p>
          </div>
        </div>
      )}

      {hasCalculated && !results && (
        <div className="borrow-result-wrap">
          <div className="result-container">
            <div className="title">Result</div>
            <p className="result-copy">
              Based on the figures entered, the goal is not reached within a
              reasonable timeframe.
            </p>

            <div className="result-divider" />

            <div className="result-note-title">PLEASE NOTE:</div>
            <p className="result-note-copy">
              Nothing on this site is personalised advice or a recommendation.
              If you need a personalised recommendation based on your personal
              circumstances you should seek financial advice.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
